import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import ComparisonVersionEvidence from './ComparisonVersionEvidence';
import {buildCommentCitationNavigation, canonicalCitations} from '../../../utils/citationNavigation';
import { Card, Typography, Tag, Button, Tooltip, Popover } from 'antd';
import {
    EllipsisOutlined,
    LinkOutlined,
    FileTextOutlined,
    TagOutlined,
    CaretDownFilled,
    AuditOutlined,
    SafetyCertificateOutlined,
    ThunderboltOutlined,
    BankOutlined,
    ReadOutlined,
    CopyOutlined,
    CheckOutlined,
    CheckCircleFilled,
    ReloadOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    LockOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AICommentCard.css';
import {useSelector} from "react-redux";
import {getCommentExpandedMarkdown, getCommentSummaryMarkdown} from "../../../utils/aiCommentFormatters";
import InlineFeedbackTrigger from "../feedback/InlineFeedbackTrigger";
import { getRecommendationSections, getRecommendationText } from "../../../utils/recommendationSections";
import { createRenderingT } from "../../../utils/renderingLabels";
import FastGlobalAnalysisPanel, {
    buildFastGlobalAnalysis,
    hasUsefulFastGlobalAnalysis,
} from './FastGlobalAnalysisPanel';
import SystemCriterionIcon, {
    SYSTEM_CRITERION_KIND,
} from './SystemCriterionIcon';
import {
    formatComparisonChangeSummary,
    getComparisonMetadata,
    isComparisonChangeComment,
    isComparisonRiskComment,
} from "../../../utils/comparisonRisk";
import { getGlobalCardPriority, globalCardNeedsConfirmation, isDirectGlobalCard } from '../../../utils/globalCardPriority';

const { Title, Text } = Typography;

// TEMP POSITIONNEMENT (2026-07): cacher "Points complémentaires" sans
// supprimer le popover ni casser l'affichage des anciennes analyses.
// Remettre à true pour le réactiver.
const SHOW_COMPLEMENTARY_INSIGHTS = false;

function normalizeRiskLevel(risk) {
    if (!risk) return null;

    const rawRisk = risk && typeof risk === 'object'
        ? risk.level || risk.risk || risk.severity
        : risk;
    const riskLower = String(rawRisk).toLowerCase().trim();

    const frenchMapping = {
        faible: 'low',
        moyen: 'medium',
        élevé: 'high',
        eleve: 'high',
        critique: 'critical',
    };

    const englishMapping = {
        low: 'low',
        medium: 'medium',
        high: 'high',
        critical: 'critical',
    };

    return frenchMapping[riskLower] || englishMapping[riskLower] || null;
}

function getRiskRationale(risk, explicitRationale, quickView) {
    const fromRisk = risk && typeof risk === 'object'
        ? risk.rationale || risk.reason || risk.why
        : null;
    const fromQuickView = quickView && typeof quickView === 'object'
        ? quickView.risk_rationale || quickView.riskRationale
        : null;
    const text = String(fromRisk || explicitRationale || fromQuickView || '')
        .replace(/\s+/g, ' ')
        .trim();
    return text || null;
}

function getRiskDisplayLabel(t, normalizedRiskLevel, labelsVariant) {
    if (!normalizedRiskLevel) return '';
    if (labelsVariant === 'entrepreneur_v1' && normalizedRiskLevel === 'critical') {
        return t('risk.entrepreneur_critical', 'Point prioritaire');
    }
    return t(`risk.${normalizedRiskLevel}`);
}

function compactComparisonText(value, maxLength = 360) {
    const text = String(value || '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength - 1).trim()}...`;
}

function isRecentlyRunning(updatedAt, timeoutMs) {
    if (!updatedAt) return false;
    return Date.now() - new Date(updatedAt).getTime() < timeoutMs;
}

function getPriorityColor(priorityLabel) {
    if (!priorityLabel) return 'default';

    const value = String(priorityLabel).toLowerCase().trim();

    if (['non négociable', 'non-negotiable', 'critical', 'critique'].includes(value)) {
        return 'red';
    }
    if (['important', 'critique business', 'haute', 'high'].includes(value)) {
        return 'orange';
    }
    if (['secondaire', 'secondary', 'moyenne', 'medium'].includes(value)) {
        return 'blue';
    }
    if (['à surveiller', 'a surveiller', 'watch', 'faible', 'low'].includes(value)) {
        return 'default';
    }

    return 'default';
}

function normalizeRiskCriticalityScore(score) {
    if (score === null || score === undefined || score === '') return null;
    const numericScore = Number(score);
    if (!Number.isFinite(numericScore)) return null;
    return Math.max(0, Math.min(100, Math.round(numericScore)));
}

function getRiskCriticalityAttentionClass(score) {
    if (score === null || score === undefined) return '';
    if (score > 75) return 'is-critical-risk-signal';
    if (score > 50) return 'is-elevated-risk-signal';
    return '';
}

function buildDetailedMarkdownFromStructured(detailedView, t, roleLabel = null) {
    if (!detailedView || typeof detailedView !== 'object') return '';

    const legalQualification = detailedView.legal_qualification || '';
    const problem = detailedView.problem || '';
    const roleRisk = Array.isArray(detailedView.role_risk) ? detailedView.role_risk : [];
    const recommendation = detailedView.recommendation || {};
    const primary = recommendation.primary || '';
    const draftingTarget = recommendation.drafting_target || '';
    const alternative = recommendation.alternative || '';
    const justification = detailedView.justification || '';

    const roleSectionTitle = roleLabel || t('ai_comment_card.role_risk', 'Risque pour le rôle');

    const parts = [];

    if (legalQualification) {
        parts.push(`**${t('ai_comment_card.qualification', 'Qualification contractuelle')} :** ${legalQualification}`);
    }

    if (problem) {
        parts.push(`**${t('ai_comment_card.problem', 'Problème')} :** ${problem}`);
    }

    parts.push(`**${roleSectionTitle}**`);

    if (roleRisk.length > 0) {
        parts.push(roleRisk.map((item) => `- ${item}`).join('\n'));
    } else {
        parts.push(`- ${t('ai_comment_card.no_role_risk', 'Risque non précisé.')}`);
    }

    parts.push(`**${t('ai_comment_card.recommendation', 'Piste de traitement')}**`);

    if (primary) {
        parts.push(`- **${t('ai_comment_card.primary_action', 'Levier prioritaire à examiner')} :** ${primary}`);
    }

    if (draftingTarget) {
        parts.push(`- **${t('ai_comment_card.drafting_target', 'Exemple de formulation à valider')} :** ${draftingTarget}`);
    }

    if (alternative) {
        parts.push(`- **${t('ai_comment_card.alternative_version', 'Option alternative à examiner')} :** ${alternative}`);
    }

    if (justification) {
        parts.push(`**${t('ai_comment_card.justification', 'Justification')} :** ${justification}`);
    }

    return parts.filter(Boolean).join('\n\n');
}
const PopoverItem = ({ label, value }) => {
    if (!value) return null;

    return (
        <div className="verdictia-popover-item">
            <Text className="verdictia-popover-label">{label}</Text>
            <Text className="verdictia-popover-value">{value}</Text>
        </div>
    );
};

const getCommentLockLabels = (t, variant = 'read') => {
    const base = 'commercial.lock.byFeature.comment_details';
    if (variant === 'more') {
        return {
            label: t(`${base}.moreCta`, { defaultValue: "Voir l'analyse approfondie" }),
            aria: t(`${base}.moreAria`, { defaultValue: "Voir l'analyse approfondie - plan supérieur" }),
        };
    }
    return {
        label: t(`${base}.readCta`, { defaultValue: "Lire l'analyse complète" }),
        aria: t(`${base}.readAria`, { defaultValue: "Lire l'analyse complète - plan supérieur" }),
    };
};

const AICommentCard = ({
                           criterionName,
                           title,
                           description,
                           date,
                           isHovered,
                           isSelected,
                           onMouseEnter,
                           onMouseLeave,
                           onClick,
                           collapsible = false,
                           disclosureOpen = false,
                           onDisclosureToggle,
                           onDisclosureOpen,
                           toggleComparisonDiffOnCardClick = false,
                           onMoreClick,
                           moreStatus,
                           moreContent,
                           pageLoading = false,
                           className = '',
                           dataRef,
                           dataCommentId,
                           doc_ref,
                           quote,
                           page,
                           commentType = null,
                           metadataJson = null,
                           onVersionEvidenceClick,
                           riskLevel,
                           riskRationale = null,
                           moreUpdatedAt,

                           // nouvelle structure métier
                           quickView = null,
                           detailedView = null,

                           // legacy / futur enrichissement
                           priorityLabel = null,
                           legalQualification = null,
                           probability = null,
                           businessImpact = null,

                           showDemoInsights = false,

                           jurisprudence = '“La suspension sans préavis a été jugée abusive en présence d’un litige sérieux sur la facture (CA Paris)',
                           legalBase = null,
                           enforceabilityScore = null,

                            // ── PR#3.4.1 : enrichissement Redux (source unique) ────────────────────
                            enrichment = {},
                            onReadClick = null,
                            readLocked = false,
                            moreLocked = false,
                            lockedTooltip = null,
                            labelsVariant = null,
                            isFastGlobal = false,
                            fastGlobalDetail = null,
                            fastGlobalOpenInPanel = false,
                            fastGlobalFollowUpDisabled = false,
                            targetBlockIds = null,
                        }) => {
    const { t } = useTranslation('review');
    const tRendering = useMemo(
        () => createRenderingT(t, labelsVariant),
        [t, labelsVariant]
    );
    const [expanded, setExpanded] = useState(false);
    const [fastGlobalExpanded, setFastGlobalExpanded] = useState(false);
    const disclosureId = useId();
    const disclosurePanelId = `comment-content-${disclosureId}`;
    const disclosureHeadingId = `comment-heading-${disclosureId}`;
    const isDisclosureOpen = !collapsible || disclosureOpen;
    const [openSections, setOpenSections] = useState({
        problem: true,
        impact: false,
        recommendation: false,
        justification: false,
    });
    const toggleSection = (key) => (event) => {
        event?.stopPropagation();
        setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };
    const ia_streaming = useSelector((state) => state.documentVersion.ia_streaming);
    const priorityComment = { metadata_json: metadataJson };
    const globalPriority = getGlobalCardPriority(priorityComment);
    const priorityReason = isDirectGlobalCard(priorityComment) && globalPriority.status === 'final'
        ? globalPriority.reason : '';
    const needsConfirmation = isDirectGlobalCard(priorityComment) && globalCardNeedsConfirmation(priorityComment);

    // ── Auto-expand quand l'enrichissement se termine ─────────────────────────
    const prevEnrichmentStatusRef = useRef(enrichment?.status);
    useEffect(() => {
        if (prevEnrichmentStatusRef.current === 'loading' && enrichment?.status === 'done') {
            setExpanded(true);
        }
        prevEnrichmentStatusRef.current = enrichment?.status;
    }, [enrichment?.status]);

    useEffect(() => {
        if (enrichment?.status === 'loading') {
            setExpanded(true);
        }
    }, [enrichment?.status]);

    const classes = ['ai-comment-card', 'has-inline-feedback'];
    if (collapsible) classes.push('is-disclosure', disclosureOpen ? 'is-disclosure-open' : 'is-disclosure-closed');
    if (isHovered) classes.push('hovered');
    if (isSelected) classes.push('selected', 'is-active');
    if (className) classes.push(className);
    const isComparisonRisk = isComparisonRiskComment({
        comment_type: commentType,
        metadata_json: metadataJson,
    });
    const systemCriterionKind = isFastGlobal
        ? SYSTEM_CRITERION_KIND.GLOBAL_REVIEW
        : isComparisonRisk
            ? SYSTEM_CRITERION_KIND.DOCUMENT_COMPARISON
            : null;
    const isComparisonChange = isComparisonChangeComment({
        comment_type: commentType,
    });
    const isCommercialPlaceholder = Boolean(
        metadataJson?.commercial_locked || metadataJson?.locked_placeholder
    );
    if (isCommercialPlaceholder) classes.push('ai-comment-card--commercial-placeholder');
    if (isComparisonRisk) classes.push('is-comparison-card');
    const comparisonMetadata = useMemo(
        () => getComparisonMetadata({ metadata_json: metadataJson }),
        [metadataJson]
    );
    const comparisonSummary = compactComparisonText(
        formatComparisonChangeSummary(
            comparisonMetadata?.change_summary,
            comparisonMetadata?.change_type,
            t
        ),
        220
    );
    const comparisonBaseText = compactComparisonText(comparisonMetadata?.base_text);
    const comparisonNewText = compactComparisonText(comparisonMetadata?.new_text);
    const comparisonContextLabel = t(
        'comparison.context_label',
        'Comparaison documentaire'
    );
    const hasComparisonDiff = Boolean(
        isComparisonRisk && (comparisonSummary || comparisonBaseText || comparisonNewText)
    );

    const normalizedRiskLevel = useMemo(
        () => normalizeRiskLevel(riskLevel),
        [riskLevel]
    );
    if (normalizedRiskLevel) {
        classes.push(`ai-comment-card--risk-${normalizedRiskLevel}`);
    }
    const normalizedTitle = String(title || '').trim();
    const isLongTitle = normalizedTitle.length > 82;
    const resolvedRiskRationale = useMemo(
        () => getRiskRationale(riskLevel, riskRationale, quickView),
        [riskLevel, riskRationale, quickView]
    );
    const riskDisplayLabel = useMemo(
        () => getRiskDisplayLabel(t, normalizedRiskLevel, labelsVariant),
        [t, normalizedRiskLevel, labelsVariant]
    );

    const RUNNING_TIMEOUT_MS = 2 * 60 * 1000;

    const hasMoreContent =
        typeof moreContent === 'string' && moreContent.trim().length > 0;

    const isMoreRunning =
        moreStatus === 'running' &&
        !hasMoreContent &&
        isRecentlyRunning(moreUpdatedAt, RUNNING_TIMEOUT_MS);
    const isMoreDisabled = ia_streaming || (isSelected && pageLoading);
    const isAnalysisInProgress = ia_streaming || pageLoading;
    const canReadComment = Boolean(onReadClick) && !isAnalysisInProgress && !isComparisonChange && !isCommercialPlaceholder;
    const readLockLabels = getCommentLockLabels(t, 'read');
    const moreLockLabels = getCommentLockLabels(t, 'more');

    const resolvedQuickView = useMemo(() => {
        if (quickView && typeof quickView === 'object') {
            return {
                risk: quickView.risk || null,
                action: quickView.action || null,
                description: quickView.description || null,
            };
        }

        if (showDemoInsights) {
            return {
                risk: 'Paiement intégral exigé malgré un service défaillant',
                action: 'Exclure faute prestataire et plafonner l’indemnité',
            };
        }

        return {
            risk: null,
            action: null,
            description: null,
        };
    }, [quickView, showDemoInsights]);

    const fastGlobalAnalysis = useMemo(() => buildFastGlobalAnalysis({
        metadataJson,
        quickView: resolvedQuickView,
        impact: businessImpact,
        quote,
        ref: doc_ref,
        page,
        targetBlockIds,
    }), [metadataJson, resolvedQuickView, businessImpact, quote, doc_ref, page, targetBlockIds]);
    const compactCriterion = isComparisonRisk ? comparisonContextLabel : criterionName;
    const compactCitations = canonicalCitations({metadataJson});
    const compactSources = compactCitations.length
        ? compactCitations.filter((citation) => citation.resolution_status !== 'unresolved')
        : fastGlobalAnalysis.sources;
    const compactSourceCount = new Set(compactSources.map((source) => JSON.stringify([
        source.document_version_id || '', source.file_id || '',
        source.citation_id || source.anchor || source.reference || '',
        source.page || '', source.excerpt || source.quote || '',
    ]))).size;
    const compactSourceLabel = compactSourceCount > 0
        ? t('collapsed_card.references', {
            count: compactSourceCount,
            defaultValue: compactSourceCount === 1 ? '1 référence' : `${compactSourceCount} références`,
        })
        : compactCitations.length
            ? t('collapsed_card.source_unavailable', 'Source à vérifier')
            : null;
    const hasCompactMetadata = !isCommercialPlaceholder && (compactCriterion || compactSourceLabel);

    // Comparison risks use the same single disclosure as Fast Global risks.
    // They must never expose the legacy "read" + "strategic memo" pair.
    const usesRiskAnalysisDisclosure = isFastGlobal || isComparisonRisk;
    const showFastGlobalAnalysis = usesRiskAnalysisDisclosure && (
        isComparisonRisk || hasUsefulFastGlobalAnalysis(fastGlobalAnalysis)
    );
    const fastGlobalPanelId = `risk-analysis-${dataCommentId || 'card'}`;

    const resolvedDetailedView = useMemo(() => {
        // Prefer the normalized enrichment cache; fall back to the server-provided
        // detailed_view so previously generated details remain readable after reload.
        if (enrichment?.detailed_view && typeof enrichment.detailed_view === 'object') {
            return enrichment.detailed_view;
        }

        if (detailedView && typeof detailedView === 'object' && enrichment?.status !== 'loading') {
            return detailedView;
        }

        if (showDemoInsights) {
            return {
                legal_qualification: 'Clause disproportionnée et potentiellement abusive.',
                problem: 'Le Client paie même en cas de manquement grave du Prestataire.',
                impact: 'Le droit de résiliation devient économiquement inutilisable.',
                role_risk: [
                    'Paiement forcé malgré un service défaillant.',
                    'Dissuasion de résilier en cas de manquements répétés.',
                ],
                recommendation: {
                    primary: 'Exclure toute indemnité en cas de faute du Prestataire.',
                    alternative: "Limiter l'indemnité à un montant forfaitaire plafonné.",
                },
                justification: 'La clause redevient négociable et compatible avec un droit de sortie crédible.',
            };
        }

        return null;
    }, [detailedView, enrichment?.detailed_view, enrichment?.status, showDemoInsights]);

    const isDirectGlobal = isFastGlobal && (
        metadataJson?.source_review_method === 'direct_original_contract'
        || metadataJson?.fast_global_card_v1?.source_review_method === 'direct_original_contract'
    );
    if (isDirectGlobal) classes.push('is-direct-global');
    const summaryMarkdown = useMemo(() => {
        // The outer disclosure already keeps direct risks compact. Opening it
        // must expose the full explanation, including exceptions and limits.
        if (isDirectGlobal) {
            return [
                description || quickView?.description || quickView?.risk || '',
                quickView?.action
                    ? `**${tRendering('ai_comment_card.action_label', 'Levier recommandé')}** - ${quickView.action}`
                    : '',
            ].filter(Boolean).join('\n\n');
        }
        return getCommentSummaryMarkdown({ quickView, quick_view: quickView, description }, tRendering);
    }, [isDirectGlobal, quickView, description, tRendering]);
    const hasExpandablePreview = Boolean(
        String(description || '').trim()
        && String(description || '').trim() !== String(summaryMarkdown || '').trim()
    );

    const mergedDetailedView = resolvedDetailedView;
    const expandedMarkdown = useMemo(
        () => getCommentExpandedMarkdown(
            { detailedView: mergedDetailedView, detailed_view: mergedDetailedView, description },
            tRendering
        ),
        [mergedDetailedView, description, tRendering]
    );

    // ── États dérivés enrichissement ─────────────────────────────────────────
    const hasDetailedContent  = Boolean(mergedDetailedView);
    const isEnrichmentLoading = enrichment?.status === 'loading';
    const isEnrichmentError   = enrichment?.status === 'error' && !hasDetailedContent;
    const [animatedFields, setAnimatedFields] = useState({});
    const [copiedRecommendationKey, setCopiedRecommendationKey] = useState(null);

    const progressiveFields = useMemo(() => ({
        problem: mergedDetailedView?.problem ? String(mergedDetailedView.problem).trim() : '',
        impact: mergedDetailedView?.impact ? String(mergedDetailedView.impact).trim() : '',
        recommendation: getRecommendationText(mergedDetailedView?.recommendation),
        justification: mergedDetailedView?.justification ? String(mergedDetailedView.justification).trim() : '',
    }), [mergedDetailedView]);

    const recommendationOptions = useMemo(() => {
        const labels = {
            primary: {
                label: t('ai_comment_card.primary_action', 'Levier prioritaire à examiner'),
                intent: t('ai_comment_card.preferred_action', 'À examiner en premier'),
            },
            drafting_target: {
                label: t('ai_comment_card.drafting_target', 'Exemple de formulation à valider'),
                intent: t('ai_comment_card.contract_wording', 'Si modification utile'),
            },
            alternative: {
                label: t('ai_comment_card.alternative_recommendation', 'Option alternative à examiner'),
                intent: t('ai_comment_card.fallback_option', 'Option alternative à examiner'),
            },
        };

        return getRecommendationSections(mergedDetailedView?.recommendation)
            .map((section) => ({
                ...section,
                label: labels[section.key]?.label || labels.alternative.label,
                intent: labels[section.key]?.intent || labels.alternative.intent,
            }));
    }, [mergedDetailedView?.recommendation, t]);

    useEffect(() => {
        if (isEnrichmentLoading && !hasDetailedContent) {
            setAnimatedFields({});
            return;
        }
        setAnimatedFields((prev) => {
            const next = { ...prev };
            for (const key of ['problem', 'impact', 'recommendation', 'justification']) {
                if (progressiveFields[key]) next[key] = true;
            }
            return next;
        });
    }, [isEnrichmentLoading, hasDetailedContent, progressiveFields]);

    const shouldRenderProgressive = expanded && (
        hasDetailedContent || (isEnrichmentLoading && !hasExpandablePreview)
    );

    const copyRecommendationText = async (text, key, event) => {
        event?.stopPropagation();
        const cleanText = String(text || '').trim();
        if (!cleanText) return;

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(cleanText);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = cleanText;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }

            setCopiedRecommendationKey(key);
            setTimeout(() => setCopiedRecommendationKey(null), 1600);
        } catch (e) {
            setCopiedRecommendationKey(null);
        }
    };

    const renderEnrichmentContent = (key, value) => {
        if (key !== 'recommendation') {
            return <div className="enrichment-section-content">{value}</div>;
        }

        if (recommendationOptions.length === 0) {
            return <div className="enrichment-section-content">{value}</div>;
        }

        return (
            <div className="enrichment-recommendation-list">
                {recommendationOptions.map((option) => (
                    <div
                        className={`enrichment-recommendation-row enrichment-recommendation-row--${option.key === 'primary' ? 'primary' : 'alternative'}`}
                        key={option.key}
                    >
                        <span className="enrichment-recommendation-label">{option.label}</span>
                        {' '}
                        <span className="enrichment-recommendation-text">{option.text}</span>
                        <Tooltip
                            title={copiedRecommendationKey === option.key
                                ? t('ai_comment_card.copied_recommendation', 'Copié')
                                : t('ai_comment_card.copy_recommendation', 'Copier cette reformulation')}
                            placement="left"
                        >
                            <Button
                                type="text"
                                className={`enrichment-recommendation-copy ${copiedRecommendationKey === option.key ? 'is-copied' : ''}`}
                                icon={copiedRecommendationKey === option.key ? <CheckOutlined /> : <CopyOutlined />}
                                onClick={(event) => copyRecommendationText(option.text, option.key, event)}
                                aria-label={t('ai_comment_card.copy_recommendation', 'Copier cette reformulation')}
                            />
                        </Tooltip>
                    </div>
                ))}
            </div>
        );
    };

    // Tooltip erreur spécifique (timeout vs erreur réseau)
    const enrichmentErrorTooltip = isEnrichmentError
        ? enrichment?.error === 'timeout'
            ? t('ai_comment_card.timeout', 'Délai dépassé — réessayer')
            : t('ai_comment_card.enrichment_error', "Erreur lors de l'enrichissement")
        : null;

    // Gestionnaire du bouton "Lire"
    const handleReadButtonClick = () => {
        if (!canReadComment) return;
        if (isEnrichmentLoading) return; // déjà en cours
        if (hasDetailedContent) {
            // Cache hit OU données initiales → toggle classique
            setExpanded((prev) => !prev);
            return;
        }
        if (readLocked) {
            onReadClick && onReadClick();
            return;
        }
        if (isEnrichmentError) {
            // Retry : relancer l'enrichissement
            onReadClick && onReadClick();
            return;
        }
        if (hasExpandablePreview) {
            setExpanded((prev) => !prev);
            onReadClick && onReadClick();
            return;
        }
        // Pas encore chargé → déclencher l'enrichissement (auto-expand à la fin)
        onReadClick && onReadClick();
    };

    const resolvedPriorityLabel =
        priorityLabel || (showDemoInsights ? 'Critique' : null);

    const resolvedLegalQualification =
        legalQualification ||
        resolvedDetailedView?.legal_qualification ||
        null;

    const popoverDetailedView =
        resolvedDetailedView ||
        (detailedView && typeof detailedView === 'object' ? detailedView : null);

    const resolvedProbability =
        probability || (showDemoInsights ? 'Élevée' : null);

    const resolvedBusinessImpact =
        businessImpact ||
        popoverDetailedView?.impact ||
        null;

    const resolvedProblem =
        popoverDetailedView?.problem ? String(popoverDetailedView.problem).trim() : '';

    const resolvedRecommendation = getRecommendationText(
        popoverDetailedView?.recommendation
    );

    const resolvedJustification =
        popoverDetailedView?.justification ? String(popoverDetailedView.justification).trim() : '';

    const riskCriticalityScore = normalizeRiskCriticalityScore(enforceabilityScore);

    const hasPopoverInsights = Boolean(
        resolvedLegalQualification ||
        resolvedProbability ||
        legalBase ||
        jurisprudence ||
        riskCriticalityScore !== null
    );

    const [insightsOpen, setInsightsOpen] = useState(false);
    const [diffOpen, setDiffOpen] = useState(false);

    const handleCardContainerClick = (event) => {
        if (collapsible && !disclosureOpen) {
            onDisclosureOpen?.(event);
        }
        if (toggleComparisonDiffOnCardClick && hasComparisonDiff) {
            event?.stopPropagation();
            setDiffOpen((prev) => !prev);
            onClick && onClick(event);
            return;
        }

        onClick && onClick(event);
    };

    const hasRiskMetrics = Boolean(resolvedProbability || resolvedBusinessImpact);
    const hasLegalReferences = Boolean(legalBase || jurisprudence);
    const hasEnforceability = riskCriticalityScore !== null;
    const riskCriticalityAttentionClass = getRiskCriticalityAttentionClass(riskCriticalityScore);
    const insightsTooltipTitle = t(
        'ai_comment_card.insights_tooltip',
        'Signaux contractuels'
    );

    const popoverContent = (
        <div className="verdictia-popover-content">
            <div className="verdictia-popover-header">
                <div className="verdictia-popover-header-icon">
                    <AuditOutlined />
                </div>
                <div className="verdictia-popover-header-text">
                    <span className="verdictia-popover-eyebrow">
                        Verdictia
                    </span>
                    <span className="verdictia-popover-title">
                        {t('ai_comment_card.insights_title', 'Points complémentaires')}
                    </span>
                </div>
            </div>

            {resolvedLegalQualification && (
                <section className="verdictia-popover-section">
                    <div className="verdictia-popover-section-title">
                        <SafetyCertificateOutlined />
                        <span>{tRendering('ai_comment_card.qualification', 'Qualification contractuelle')}</span>
                    </div>
                    <p className="verdictia-popover-section-body">
                        {resolvedLegalQualification}
                    </p>
                </section>
            )}

            {resolvedProbability && (
                <section className="verdictia-popover-section">
                    <div className="verdictia-popover-section-title">
                        <ThunderboltOutlined />
                        <span>{t('ai_comment_card.risk_section', 'Évaluation du risque')}</span>
                    </div>
                    <div className="verdictia-popover-metric-grid">
                        <div className="verdictia-popover-metric">
                            <span className="verdictia-popover-metric-label">
                                {t('ai_comment_card.probability', 'Probabilité')}
                            </span>
                            <span className="verdictia-popover-metric-value">
                                {resolvedProbability}
                            </span>
                        </div>
                    </div>
                </section>
            )}


            {hasLegalReferences && (
                <section className="verdictia-popover-section">
                    <div className="verdictia-popover-section-title">
                        <BankOutlined />
                        <span>{tRendering('ai_comment_card.legal_references', 'Sources de référence')}</span>
                    </div>

                    {legalBase && (
                        <div className="verdictia-popover-reference">
                            <span className="verdictia-popover-reference-chip">
                                {t('ai_comment_card.legal_base', 'Références possibles')}
                            </span>
                            <span className="verdictia-popover-reference-text">
                                {legalBase}
                            </span>
                        </div>
                    )}

                    {jurisprudence && (
                        <div className="verdictia-popover-reference">
                            <span className="verdictia-popover-reference-chip verdictia-popover-reference-chip--muted">
                                <ReadOutlined />
                                {t('ai_comment_card.jurisprudence', "Point d'attention")}
                            </span>
                            <span className="verdictia-popover-reference-text verdictia-popover-quote">
                                « {jurisprudence} »
                            </span>
                        </div>
                    )}
                </section>
            )}

            {hasEnforceability && (
                <section className={`verdictia-popover-section verdictia-popover-section--score ${riskCriticalityAttentionClass}`}>
                    <div className="verdictia-popover-score-head">
                        <span className="verdictia-popover-score-label-wrap">
                            <Text className="verdictia-popover-label">
                            {t('ai_comment_card.enforceability', "Niveau d'attention")}
                        </Text>
                            <Tooltip
                                placement="bottom"
                                overlayClassName="verdictia-enforceability-tooltip"
                                title={t(
                                    'ai_comment_card.enforceability_explanation',
                                    "Score indicatif IA du niveau d'attention. Il mesure l'importance du point detecte pour la partie analysee, selon la gravite juridique, l'impact economique ou operationnel, la probabilite pratique, le desequilibre contractuel et la difficulte a corriger ou negocier le point. Ce n'est pas une probabilite judiciaire."
                                )}
                            >
                                <InfoCircleOutlined className="verdictia-popover-info-icon" />
                            </Tooltip>
                        </span>
                        <Text className="verdictia-popover-value verdictia-popover-score-value">
                            {riskCriticalityScore}
                            <small>/100</small>
                        </Text>
                    </div>
                    <div className="verdictia-score-track">
                        <div
                            className="verdictia-score-fill"
                            style={{ width: `${riskCriticalityScore}%` }}
                        />
                    </div>
                </section>
            )}

            <div className="verdictia-popover-footer">
                <CheckCircleFilled className="verdictia-popover-footer-icon" />
                <span>
                    {t(
                        'ai_comment_card.insights_footer',
                        'Analyse générée à titre indicatif — validation professionnelle recommandée.'
                    )}
                </span>
            </div>
        </div>
    );


    return (
        <Card
            className={classes.join(' ')}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={handleCardContainerClick}
            data-ref={dataRef}
            data-comment-id={dataCommentId}
            size="small"
            bodyStyle={{
                padding: '10px 12px 8px 10px',
                position: 'relative',
                overflow: 'visible',
            }}
        >
            {!collapsible && <div className="bg-pattern" />}

            {collapsible && (
                <h5 className="comment-disclosure-heading">
                    <button
                        type="button"
                        id={disclosureHeadingId}
                        className="comment-disclosure-toggle"
                        aria-labelledby={`${disclosureHeadingId}-title`}
                        aria-describedby={hasCompactMetadata ? `${disclosureHeadingId}-metadata` : undefined}
                        aria-expanded={disclosureOpen}
                        aria-controls={disclosurePanelId}
                        onClick={(event) => {
                            event.stopPropagation();
                            onDisclosureToggle?.(event);
                        }}
                    >
                        <span className="comment-disclosure-summary">
                            <span id={`${disclosureHeadingId}-title`} className="comment-disclosure-title" title={normalizedTitle}>{normalizedTitle}</span>
                            {hasCompactMetadata && (
                                <span id={`${disclosureHeadingId}-metadata`} className="comment-disclosure-metadata">
                                    {compactCriterion && (
                                        <span className="comment-disclosure-criterion" title={compactCriterion}>
                                            <TagOutlined aria-hidden="true" />
                                            <span>{compactCriterion}</span>
                                        </span>
                                    )}
                                    {compactSourceLabel && (
                                        <span className="comment-disclosure-source">
                                            <FileTextOutlined aria-hidden="true" />
                                            <span>{compactSourceLabel}</span>
                                        </span>
                                    )}
                                </span>
                            )}
                        </span>
                        <span className="comment-disclosure-chevron" aria-hidden="true">
                            <svg viewBox="0 0 20 20" fill="none">
                                <path d="m7.5 5 5 5-5 5" />
                            </svg>
                        </span>
                    </button>
                </h5>
            )}

            {(priorityReason || needsConfirmation) && (
                <div className="global-card-priority-context">
                    {priorityReason && normalizedRiskLevel && (
                        <div className="global-card-priority-heading">
                            <span className={`verdictia-risk-badge risk-${normalizedRiskLevel}`}>
                                <span className="verdictia-risk-badge-value">
                                    {t(`ai_comment_card.priority_levels.${normalizedRiskLevel}`)}
                                </span>
                            </span>
                            {globalPriority.rank && <span className="global-card-priority-rank">
                                {t('ai_comment_card.priority_rank', { count: globalPriority.rank,
                                    defaultValue: `Rang ${globalPriority.rank}` })}
                            </span>}
                        </div>
                    )}
                    {needsConfirmation && <Tag>{t('ai_comment_card.to_confirm', 'À confirmer')}</Tag>}
                    {priorityReason && <p className="global-card-priority-reason">
                        <strong>{t('ai_comment_card.priority_reason', 'Pourquoi cette priorité')}</strong>
                        {' · '}{priorityReason}
                    </p>}
                </div>
            )}

            <div
                id={disclosurePanelId}
                className={`comment-disclosure-body ${isDisclosureOpen ? 'is-open' : ''}`}
                role={collapsible ? 'region' : undefined}
                aria-labelledby={collapsible ? disclosureHeadingId : undefined}
                aria-hidden={collapsible ? !disclosureOpen : undefined}
                inert={collapsible && !disclosureOpen ? true : undefined}
            >
            <div className="comment-disclosure-inner">
            <div className="tags-section">
                <div className="tags-main">
                    {SHOW_COMPLEMENTARY_INSIGHTS && hasPopoverInsights && (
                        <span className="verdictia-insights-inline" onClick={(e) => e.stopPropagation()}>
                            <Popover
                                content={popoverContent}
                                trigger={['hover', 'click']}
                                placement="leftTop"
                                overlayClassName="verdictia-insights-popover"
                                open={insightsOpen}
                                onOpenChange={setInsightsOpen}
                                destroyTooltipOnHide
                                autoAdjustOverflow
                                align={{ offset: [-8, -4] }}
                            >
                                <Tooltip
                                    title={
                                        insightsOpen
                                            ? null
                                            : insightsTooltipTitle
                                    }
                                    placement="top"
                                    mouseEnterDelay={0.3}
                                >
                                    <Button
                                        size="small"
                                        type="text"
                                        className={`verdictia-insights-button ${riskCriticalityAttentionClass} ${
                                            insightsOpen ? 'is-open' : ''
                                        }`}
                                        aria-label={insightsTooltipTitle}
                                        aria-expanded={insightsOpen}
                                    >
                                        <SafetyCertificateOutlined />
                                    </Button>
                                </Tooltip>
                            </Popover>
                        </span>
                    )}
                    {criterionName && !isComparisonRisk && (
                        <span className="criterion-identity">
                            <SystemCriterionIcon
                                kind={systemCriterionKind}
                                criterionName={criterionName}
                            />
                            <Tag className="criterion-tag" title={criterionName}>
                                <span className="criterion-tag__label">{criterionName}</span>
                            </Tag>
                        </span>
                    )}
                    {isCommercialPlaceholder && (
                        <Tag className="commercial-placeholder-tag" icon={<LockOutlined />}>
                            {t('commercial.lockedCommentTag', 'Plan supérieur')}
                        </Tag>
                    )}
                    {isComparisonRisk && (
                        <span className="comparison-context-label">
                            <SystemCriterionIcon
                                kind={SYSTEM_CRITERION_KIND.DOCUMENT_COMPARISON}
                                criterionName={comparisonContextLabel}
                            />
                            <span>{comparisonContextLabel}</span>
                        </span>
                    )}
                    {doc_ref && !isComparisonRisk && !canonicalCitations({metadataJson}).length && (
                        <Tooltip
                            title={
                                <div style={{ maxWidth: 500 }}>
                                    {quote && (
                                        <div style={{ marginBottom: 8 }}>
                                            <Text style={{ color: '#fff', fontStyle: 'italic' }}>
                                                "{quote}"
                                            </Text>
                                        </div>
                                    )}
                                    {page && (
                                        <div>
                                            <Text style={{ color: '#ccc', fontSize: 12 }}>
                                                {t('page')} {page}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            }
                            placement="topLeft"
                            overlayStyle={{ maxWidth: 350 }}
                        >
                            <Tag
                                icon={<LinkOutlined style={{ fontSize: 8 }} />}
                                className="reference-tag"
                            >
                                {doc_ref}
                            </Tag>
                        </Tooltip>
                    )}
                </div>

                {(resolvedPriorityLabel || normalizedRiskLevel) && (
                    <div
                        className="verdictia-top-right"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {resolvedPriorityLabel && (
                            <Tag
                                color={getPriorityColor(resolvedPriorityLabel)}
                                className="verdictia-priority-tag"
                            >
                                {resolvedPriorityLabel}
                            </Tag>
                        )}

                        {normalizedRiskLevel && (
                            <Tooltip
                                title={resolvedRiskRationale}
                                placement="topRight"
                                overlayClassName="verdictia-risk-rationale-tooltip"
                            >
                                <div
                                    className={`verdictia-risk-badge risk-${normalizedRiskLevel} ${resolvedRiskRationale ? 'has-rationale' : ''}`}
                                    aria-label={resolvedRiskRationale || riskDisplayLabel}
                                >
                                    <span className="verdictia-risk-badge-value">
                                        {riskDisplayLabel}
                                    </span>
                                    {resolvedRiskRationale && (
                                        <InfoCircleOutlined className="verdictia-risk-badge-info" />
                                    )}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>

            {!collapsible && <div className={`card-title-frame ${isLongTitle ? 'is-long' : ''}`} title={normalizedTitle}>
                <Title level={5} className="card-title">
                    {normalizedTitle}
                </Title>
            </div>}

            {isCommercialPlaceholder && (
                <div className="commercial-placeholder-banner">
                    <LockOutlined />
                    <span>
                        {t(
                            'commercial.lockedCommentBody',
                            'Aperçu disponible avec Solo / Pro. Le contenu réel n’est pas envoyé avec le plan actuel.'
                        )}
                    </span>
                </div>
            )}

            <div
                className={`comment-content-shell ${(expanded || shouldRenderProgressive) ? 'is-expanded' : ''}`}
            >
                {shouldRenderProgressive ? (
                    <div className="enrichment-panel">
                        {isEnrichmentLoading && (
                            <div className="enrichment-streaming-hint">
                                <span className="typing-dot" />
                                <span>{t('ai_comment_card.loading', 'Chargement...')}</span>
                            </div>
                        )}

                        {[
                            { key: 'problem', title: t('ai_comment_card.problem', 'Problème'), icon: <ExclamationCircleOutlined /> },
                            { key: 'impact', title: t('ai_comment_card.impact', 'Impact'), icon: <ThunderboltOutlined /> },
                            { key: 'recommendation', title: t('ai_comment_card.recommendation', 'Piste de traitement'), icon: <ReadOutlined /> },
                            { key: 'justification', title: t('ai_comment_card.justification', 'Justification'), icon: <AuditOutlined /> },
                        ].map((section) => {
                            const value = progressiveFields[section.key];
                            const hasValue = Boolean(value);
                            if (!hasValue) return null;
                            const isOpen = Boolean(openSections[section.key]);
                            return (
                                <div
                                    key={section.key}
                                    className={`enrichment-section-card ${animatedFields[section.key] ? 'is-in' : ''} ${isOpen ? 'is-open' : 'is-collapsed'}`}
                                >
                                    <button
                                        type="button"
                                        className="enrichment-section-head"
                                        onClick={toggleSection(section.key)}
                                        aria-expanded={isOpen}
                                        aria-controls={`enrichment-body-${section.key}`}
                                    >
                                        <span className="enrichment-section-icon">{section.icon}</span>
                                        <span className="enrichment-section-title">{section.title}</span>
                                        <CaretDownFilled
                                            className={`enrichment-section-chevron ${isOpen ? 'is-open' : ''}`}
                                        />
                                    </button>
                                    <div
                                        id={`enrichment-body-${section.key}`}
                                        className={`enrichment-section-body ${isOpen ? 'is-open' : ''}`}
                                    >
                                        {renderEnrichmentContent(section.key, value)}
                                    </div>
                                </div>
                            );
                        })}

                    </div>
                ) : (
                    <div className={`card-description comment-preview ${expanded ? 'expanded' : ''}`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {expanded ? expandedMarkdown : summaryMarkdown}
                        </ReactMarkdown>
                    </div>
                )}
            </div>

            {showFastGlobalAnalysis && fastGlobalExpanded && (
                <FastGlobalAnalysisPanel
                    id={fastGlobalPanelId}
                    analysis={fastGlobalAnalysis}
                    detail={fastGlobalDetail}
                    onRetry={onMoreClick}
                />
            )}

            {canonicalCitations({metadataJson}).length > 0 && buildCommentCitationNavigation({id: dataCommentId, metadataJson})?.unavailable && (
                <small className="citation-location-notice">Localisation à vérifier</small>
            )}
            {isComparisonRisk && (
                <ComparisonVersionEvidence
                    metadata={comparisonMetadata}
                    onEvidenceOpen={onVersionEvidenceClick}
                />
            )}
            {hasComparisonDiff && (
                <div className={`comparison-diff-panel ${diffOpen ? 'is-open' : 'is-collapsed'}`}>
                    <button
                        type="button"
                        className={`comparison-diff-toggle ${diffOpen ? 'is-open' : ''} ${comparisonSummary ? 'has-summary' : ''}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            setDiffOpen((prev) => !prev);
                        }}
                        aria-expanded={diffOpen}
                        aria-controls={`comparison-diff-grid-${dataCommentId || 'card'}`}
                        title={comparisonSummary || undefined}
                    >
                        {comparisonSummary ? (
                            <>
                                <span className="comparison-diff-toggle__eyebrow">
                                    {t('comparison.change_label', 'Changement')}
                                </span>
                                <span className="comparison-diff-toggle__summary">
                                    {comparisonSummary}
                                </span>
                            </>
                        ) : (
                            <span className="comparison-diff-toggle__label">
                                {diffOpen
                                    ? t('comparison.hide_diff', 'Masquer la comparaison')
                                    : t('comparison.show_diff', 'Voir la comparaison')}
                            </span>
                        )}
                        <CaretDownFilled className="comparison-diff-toggle__chevron" />
                    </button>

                    <div
                        id={`comparison-diff-grid-${dataCommentId || 'card'}`}
                        className="comparison-diff-grid-wrapper"
                        aria-hidden={!diffOpen}
                    >
                        <div className="comparison-diff-grid">
                            {comparisonBaseText && (
                                <div className="comparison-diff-column">
                                    <span className="comparison-diff-label">
                                        {t('comparison.reference', 'Référence')}
                                    </span>
                                    <p>{comparisonBaseText}</p>
                                </div>
                            )}
                            {comparisonNewText && (
                                <div className="comparison-diff-column comparison-diff-column--received">
                                    <span className="comparison-diff-label">
                                        {t('comparison.received_version', 'Version reçue')}
                                    </span>
                                    <p>{comparisonNewText}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className="card-footer">
                <div className="footer-info">
                    <Text type="secondary" className="date-text">
                        {date}
                    </Text>

                    {page && (
                        <Text type="secondary" className="page-text">
                            • {t('page_short')} {page}
                        </Text>
                    )}
                </div>

                <div
                    className="card-actions"
                    onClick={(e) => e.stopPropagation()}
                >
                    {!isCommercialPlaceholder && (
                        <InlineFeedbackTrigger
                            featureArea="ai_comment"
                            targetType="comment"
                            targetId={dataCommentId}
                            aiCommentId={dataCommentId}
                            contextJson={{
                                source: "ai_comment_card",
                                comment_title: title,
                                criterion_title: criterionName,
                                risk_level: normalizedRiskLevel || null,
                                comment_type: commentType || null,
                            }}
                            badgeLabel={t("feedback.badges.ai_comment")}
                            tooltip={t("feedback.commentTooltip")}
                        />
                    )}
                    {/* ── Bouton "Lire" : enrichissement ou toggle ────────── */}
                    {!usesRiskAnalysisDisclosure && canReadComment && (
                        <Tooltip
                                                title={readLocked ? lockedTooltip : enrichmentErrorTooltip}
                                                placement="top"
                                            >
                                                <Button
                                                    type="default"
                                                    size="small"
                                                    onClick={handleReadButtonClick}
                                                    loading={!readLocked && isEnrichmentLoading}
                                                    disabled={!readLocked && isEnrichmentLoading}
                                                    className={`expand-inline-button ${(expanded && !isEnrichmentLoading) ? 'expanded' : ''} ${isEnrichmentError ? 'enrichment-error' : ''} ${readLocked ? 'is-commercial-locked' : ''}`}
                                                    aria-label={isEnrichmentLoading
                                                        ? t('ai_comment_card.loading', 'Chargement...')
                                                        : isEnrichmentError
                                                            ? t('ai_comment_card.retry', 'Réessayer')
                                                            : readLocked
                                                                ? readLockLabels.aria
                                                                : expanded
                                                                ? t('ai_comment_card.collapse')
                                                                : t('ai_comment_card.read')
                                                    }
                                                >
                                                    {isEnrichmentError ? (
                                                        <>
                                                            <ReloadOutlined style={{ fontSize: 10, color: '#ff4d4f' }} />
                                                            <span style={{ color: '#ff4d4f' }}>
                                                                {t('ai_comment_card.retry', 'Réessayer')}
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="expand-inline-button__content">
                                                                {readLocked ? (
                                                                    <LockOutlined className="expand-inline-button__lead-icon" />
                                                                ) : (
                                                                    <ReadOutlined className="expand-inline-button__lead-icon" />
                                                                )}
                                                                <span className="expand-inline-button__label">
                                                                    {readLocked
                                                                        ? readLockLabels.label
                                                                        : isEnrichmentLoading
                                                                        ? t('ai_comment_card.loading', 'Chargement...')
                                                                        : expanded
                                                                            ? t('ai_comment_card.collapse')
                                                                            : t('ai_comment_card.read')}
                                                                </span>
                                                            </span>
                                                            {!isEnrichmentLoading && (
                                                                <span
                                                                    className={`expand-inline-button__chevron ${expanded ? 'is-open' : ''}`}
                                                                    aria-hidden="true"
                                                                >
                                                                    <CaretDownFilled />
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </Button>
                                            </Tooltip>
                    )}

                    {!usesRiskAnalysisDisclosure && onMoreClick && !isComparisonChange && (
                        <Button
                            type="default"
                            size="small"
                            icon={moreLocked
                                ? <LockOutlined style={{ fontSize: 13, fontWeight: 700 }} />
                                : <EllipsisOutlined style={{ fontSize: 16, fontWeight: 700 }} />}
                            onClick={onMoreClick}
                            className={`more-button ${moreLocked ? 'is-commercial-locked' : ''}`}
                            disabled={isMoreDisabled}
                            loading={!moreLocked && isMoreRunning}
                        >
                            {moreLocked
                                ? moreLockLabels.label
                                : t('ai_comment_card.ai_analysis')}
                        </Button>
                    )}
                    {showFastGlobalAnalysis && (
                        <Button
                            type="default"
                            size="small"
                            icon={<ReadOutlined />}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (fastGlobalOpenInPanel) {
                                    if (onMoreClick) onMoreClick();
                                    return;
                                }
                                setFastGlobalExpanded((previous) => {
                                    if (!previous && onMoreClick) onMoreClick();
                                    return !previous;
                                });
                            }}
                            className={`fast-global-analysis-button ${fastGlobalExpanded ? 'is-open' : ''}`}
                            aria-label={t('ai_comment_card.analysis.open')}
                            disabled={fastGlobalFollowUpDisabled}
                            title={fastGlobalFollowUpDisabled
                                ? t('ai_comment_card.analysis.availableAfterSave', 'Disponible une fois l’analyse terminée et enregistrée.')
                                : undefined}
                            aria-expanded={fastGlobalOpenInPanel ? undefined : fastGlobalExpanded}
                            aria-controls={fastGlobalOpenInPanel ? undefined : fastGlobalPanelId}
                        >
                            {t('ai_comment_card.analysis.open')}
                            {!fastGlobalOpenInPanel && <CaretDownFilled aria-hidden="true" className={`fast-global-analysis-button__chevron ${fastGlobalExpanded ? 'is-open' : ''}`} />}
                        </Button>
                    )}
                </div>
            </div>
            </div>
            </div>
        </Card>
    );
};

export default AICommentCard;
